import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, Bell, Mail, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api, { unwrap } from '../../services/api';

const BREADCRUMBS = {
  '/dashboard':        'Dashboard',
  '/properties':       'Properties',
  '/units':            'Units',
  '/tenants':          'Tenants',
  '/leases':           'Leases',
  '/applications':     'Applications',
  '/invoices':         'Invoices',
  '/payments':         'Payments',
  '/mpesa-logs':       'M-Pesa Logs',
  '/utilities':        'Utility Bills',
  '/deposits':         'Security Deposits',
  '/maintenance':      'Maintenance',
  '/vendors':          'Vendors',
  '/inspections':      'Inspections',
  '/analytics':        'Reports & Analytics',
  '/collections':      'Collections Aging',
  '/users':            'User Management',
  '/settings':         'Settings',
  '/messages':         'Messages',
  '/announcements':    'Announcements',
  '/budgets':          'Budgets',
  '/expenses':         'Expenses',
  '/short-term':       'Short-term Rentals',
  '/vacate-notices':   'Vacate Notices',
  '/vacancy-listings': 'Vacancy Listings',
  '/alert-rules':      'Alert Rules',
  '/my-lease':         'My Lease',
  '/payment-history':  'Payment History',
  '/report-issue':     'Report Issue',
  '/my-maintenance':   'My Requests',
  '/notice-to-vacate': 'Notice to Vacate',
  '/my-tasks':         'My Tasks',
  '/platform':         'Platform Overview',
  '/accounts':         'Accounts',
  '/prospects':        'Prospects',
  '/audit-logs':       'Audit Log',
  '/staff':            'Staff',
  '/import':           'Bulk Import',
  '/billing':          'Billing',
};

// ── Search Bar ─────────────────────────────────────────────────────────────────
function SearchBar({ onClose }) {
  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        // Search across properties, tenants, units
        const [pRes, tRes, uRes] = await Promise.allSettled([
          api.get('/properties/properties/', { params: { search: query, limit: 3 } }),
          api.get('/accounts/users/', { params: { search: query, role: 'tenant', limit: 3 } }),
          api.get('/properties/units/', { params: { search: query, limit: 3 } }),
        ]);
        const hits = [];
        if (pRes.status === 'fulfilled') {
          (unwrap(pRes.value) || []).forEach(p => hits.push({ label: p.name, sub: 'Property', path: '/properties' }));
        }
        if (tRes.status === 'fulfilled') {
          (unwrap(tRes.value) || []).forEach(t => hits.push({ label: t.email, sub: 'Tenant', path: '/tenants' }));
        }
        if (uRes.status === 'fulfilled') {
          (unwrap(uRes.value) || []).forEach(u => hits.push({ label: `Unit ${u.unit_number}`, sub: u.property_name || 'Unit', path: '/units' }));
        }
        setResults(hits);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-20 px-4"
         onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          <Search size={18} className="text-gray-400"/>
          <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search properties, tenants, units..."
            className="flex-1 text-sm outline-none text-gray-900 placeholder-gray-400"/>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X size={16} className="text-gray-400"/>
          </button>
        </div>
        {loading && <div className="px-4 py-3 text-xs text-gray-400">Searching...</div>}
        {!loading && results.length > 0 && (
          <div className="divide-y divide-gray-50 max-h-72 overflow-y-auto">
            {results.map((r, i) => (
              <button key={i} onClick={() => { navigate(r.path); onClose(); }}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">{r.label}</p>
                  <p className="text-xs text-gray-400">{r.sub}</p>
                </div>
              </button>
            ))}
          </div>
        )}
        {!loading && query && results.length === 0 && (
          <div className="px-4 py-6 text-center text-sm text-gray-400">No results for "{query}"</div>
        )}
        {!query && (
          <div className="px-4 py-6 text-center text-sm text-gray-400">Type to search across LPMS</div>
        )}
      </div>
    </div>
  );
}

// ── Main TopHeader ─────────────────────────────────────────────────────────────
export default function TopHeader({ onMenuClick }) {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const [unreadNotif, setUnreadNotif] = useState(0);
  const [unreadMsg,   setUnreadMsg]   = useState(0);
  const [showSearch,  setShowSearch]  = useState(false);

  const current = BREADCRUMBS[location.pathname] || 'Page';

  // Build correct initials from user profile
  const profile   = user?.profile || {};
  const firstName = profile.first_name || user?.first_name || '';
  const lastName  = profile.last_name  || user?.last_name  || '';
  const initials  = firstName && lastName
    ? `${firstName[0]}${lastName[0]}`.toUpperCase()
    : firstName
      ? firstName.slice(0,2).toUpperCase()
      : (user?.email || 'U').slice(0,2).toUpperCase();

  // Load unread counts
  useEffect(() => {
    async function loadCounts() {
      try {
        const [nRes, mRes] = await Promise.allSettled([
          api.get('/notifications/unread-count/'),
          api.get('/communications/messages/unread-count/'),
        ]);
        if (nRes.status === 'fulfilled') {
          const d = nRes.value?.data;
          setUnreadNotif(d?.data?.unread_count ?? d?.unread_count ?? 0);
        }
        if (mRes.status === 'fulfilled') {
          const d = mRes.value?.data;
          setUnreadMsg(d?.data?.unread_count ?? d?.unread_count ?? 0);
        }
      } catch { }
    }
    loadCounts();
    // Refresh every 60 seconds
    const interval = setInterval(loadCounts, 60000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  return (
    <>
      <header className="sticky top-0 z-30 h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 lg:px-8">
        {/* Left — breadcrumb */}
        <div className="flex items-center gap-4">
          <button onClick={onMenuClick}
            className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg">
            <span className="sr-only">Menu</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <nav className="hidden sm:flex items-center gap-2 text-sm text-gray-500">
            <span>Home</span>
            <span className="text-gray-300">/</span>
            <span className="text-gray-900 font-semibold">{current}</span>
          </nav>
        </div>

        {/* Right — actions */}
        <div className="flex items-center gap-2">

          {/* Search */}
          <button onClick={() => setShowSearch(true)}
            className="w-10 h-10 rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 flex items-center justify-center transition-colors"
            title="Search">
            <Search size={18} />
          </button>

          {/* Notifications */}
          <button onClick={() => navigate('/settings')}
            className="w-10 h-10 rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 flex items-center justify-center transition-colors relative"
            title="Notifications">
            <Bell size={18} />
            {unreadNotif > 0 && (
              <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 bg-red-500 rounded-full border-2 border-white flex items-center justify-center">
                <span className="text-white text-[9px] font-bold px-0.5">{unreadNotif > 9 ? '9+' : unreadNotif}</span>
              </span>
            )}
          </button>

          {/* Messages */}
          <button onClick={() => navigate('/messages')}
            className="w-10 h-10 rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 flex items-center justify-center transition-colors relative"
            title="Messages">
            <Mail size={18} />
            {unreadMsg > 0 && (
              <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 bg-blue-500 rounded-full border-2 border-white flex items-center justify-center">
                <span className="text-white text-[9px] font-bold px-0.5">{unreadMsg > 9 ? '9+' : unreadMsg}</span>
              </span>
            )}
          </button>

          {/* Avatar — goes to settings */}
          <button onClick={() => navigate('/settings')}
            className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity"
            title="Profile & Settings">
            {initials}
          </button>
        </div>
      </header>

      {/* Search modal */}
      {showSearch && <SearchBar onClose={() => setShowSearch(false)} />}
    </>
  );
}
